import { useParams } from 'react-router-dom'
import { useFacilities } from '@/hooks/useBanking'
import type { FacilityFinancialAccountsScope } from '@/components/financial-accounts/financialAccountsScope'

/**
 * The facility scope of the current route, read from its `:facilityId` param.
 *
 * Returns `scope: null` on the organization-wide routes (no param). On a
 * facility route the scope resolves once facilities have loaded; `notFound`
 * is set when the id matches no facility.
 */
export function useFacilityScope(): {
  scope: FacilityFinancialAccountsScope | null
  /** True on a facility route, even before the facility has loaded. */
  scoped: boolean
  isLoading: boolean
  error: Error | null
  notFound: boolean
} {
  const { facilityId } = useParams<{ facilityId: string }>()
  // Inactive facilities included: their page is reachable (read-only).
  const facilities = useFacilities({ activeOnly: false })
  if (!facilityId) {
    return { scope: null, scoped: false, isLoading: false, error: null, notFound: false }
  }
  const facility = facilities.data?.find((f) => f.id === facilityId)
  return {
    scope: facility
      ? { facilityId: facility.id, facilityName: facility.name, readOnly: !facility.is_active }
      : null,
    scoped: true,
    isLoading: facilities.isLoading,
    error: facilities.data ? null : facilities.error,
    notFound: !!facilities.data && !facility,
  }
}
